import React, { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { adminApi } from '../lib/adminApi';
import { CreditCard, RefreshCw, Undo2, Filter, X, IndianRupee } from 'lucide-react';

const STATUS_COLORS = {
  created: 'bg-gray-100 text-gray-700',
  captured: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-800',
  refunded: 'bg-amber-100 text-amber-800',
  signature_failed: 'bg-red-100 text-red-800',
};

const AdminPaymentsPanel = () => {
  const { toast } = useToast();
  const [data, setData] = useState({ items: [], total: 0, captured_total_inr: 0, captured_count: 0 });
  const [filters, setFilters] = useState({ status: '', type: '', vendor_id: '' });
  const [loading, setLoading] = useState(true);
  const [refunding, setRefunding] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await adminApi.listPayments(params);
      setData(r);
    } catch (err) {
      toast({ title: 'Failed to load payments', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [filters, toast]);

  useEffect(() => { load(); }, [load]);

  const refund = async (order) => {
    if (!window.confirm(`Refund ₹${(order.amount / 100).toLocaleString('en-IN')} for ${order.vendor_name}?`)) return;
    setRefunding(order.id);
    try {
      await adminApi.refundPayment(order.id, { reason: 'Admin-initiated refund' });
      toast({ title: 'Refund initiated', description: 'Razorpay will process the refund within 5–10 business days.' });
      load();
    } catch (err) {
      toast({ title: 'Refund failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setRefunding(null); }
  };

  const fmtAmount = (paise) => `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN') : '—';

  return (
    <div className="space-y-4" data-testid="admin-payments-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#2c54b2]" /> Payments
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Razorpay transactions across all vendors. Includes plan upgrades and lead credit top-ups.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowFilters((v) => !v)} data-testid="payments-filter-toggle">
            <Filter className="w-3.5 h-3.5 mr-1" /> Filters
          </Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="payments-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Captured (filtered)</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1 flex items-center gap-1">
            <IndianRupee className="w-5 h-5" />{data.captured_total_inr?.toLocaleString('en-IN') || 0}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">{data.captured_count || 0} successful transactions</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Total records</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{data.total || 0}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Mode in use</div>
          <div className="text-sm font-mono text-gray-900 mt-2">
            {(data.items || [])[0]?.mode === 'live' ? <span className="text-emerald-700 font-semibold">LIVE</span> : <span className="text-amber-700 font-semibold">TEST</span>}
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-md p-3 flex flex-wrap items-end gap-3" data-testid="payments-filters">
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs text-gray-600 block">Status</label>
            <select className="border rounded h-9 px-2 text-sm w-full" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status">
              <option value="">All</option><option>created</option><option>captured</option><option>failed</option><option>refunded</option>
            </select>
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs text-gray-600 block">Type</label>
            <select className="border rounded h-9 px-2 text-sm w-full" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} data-testid="filter-type">
              <option value="">All</option><option value="plan">Plan upgrade</option><option value="credits">Lead credits</option>
            </select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-xs text-gray-600 block">Vendor ID</label>
            <Input value={filters.vendor_id} onChange={(e) => setFilters({ ...filters, vendor_id: e.target.value })} placeholder="v-xxxxxxxx" data-testid="filter-vendor" />
          </div>
          <Button size="sm" variant="ghost" onClick={() => setFilters({ status: '', type: '', vendor_id: '' })} data-testid="filter-clear">
            <X className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading…</div>
        ) : (data.items || []).length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No payment records yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-xs uppercase tracking-wider text-gray-600">
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Vendor</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Mode</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((o) => {
                  const colorCls = STATUS_COLORS[o.status] || 'bg-gray-100 text-gray-700';
                  return (
                    <tr key={o.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`payment-row-${o.id}`}>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(o.created_at)}</td>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-medium">{o.vendor_name}</div>
                        <div className="text-gray-500 font-mono text-[10px]">{o.vendor_id}</div>
                      </td>
                      <td className="px-3 py-2"><span className="text-[10px] uppercase font-bold tracking-wider bg-blue-50 text-[#2c54b2] px-1.5 py-0.5 rounded">{o.type}</span></td>
                      <td className="px-3 py-2 text-xs">{o.description}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{fmtAmount(o.amount)}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${colorCls}`}>{o.status}</span></td>
                      <td className="px-3 py-2 text-xs uppercase font-semibold text-gray-500">{o.mode}</td>
                      <td className="px-3 py-2 text-right">
                        {o.status === 'captured' && (
                          <Button size="sm" variant="outline" onClick={() => refund(o)} disabled={refunding === o.id} className="text-amber-700 border-amber-200 hover:bg-amber-50" data-testid={`refund-${o.id}`}>
                            <Undo2 className="w-3 h-3 mr-1" /> {refunding === o.id ? '…' : 'Refund'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPaymentsPanel;
